import styled from '@emotion/styled'
import Label from '../inputs/Label'
import Text from '../inputs/Text'
import React, {useEffect, useState} from 'react'
import {useLazyQuery} from '@apollo/client'
import tw from 'twin.macro'
import {useSessionStorageState} from '../../utils/useSessionStorageState'
import {lookupStorageKey} from '../../apollo/cache'
import {OrdersByTikTokOrderIdQuery} from '../../apollo/queries'
import {Loading} from '../Loading'
import Button from '../Button'
import ShadowBox from '../layout/ShadowBox'
import Subtitle from '../headings/Subtitle'
import H2 from '../headings/H2'
import Image from 'next/image'

const Div = styled.div(tw`relative mb-6`)

const initialState = {
  authenticated: false,
  tiktokOrderId: '',
  orderNumber: '',
  order: '',
  timestamp: 0,
}

const Lookup = () => {
  const [state, setState] = useSessionStorageState(
    lookupStorageKey,
    initialState,
  )
  const [error, setError] = useState('')
  const [showLogin, setShowLogin] = useState(false)

  useEffect(() => {
    if (state.authenticated && state.orderNumber) {
      window.location.replace(`/order/${state.orderNumber}`)
      return
    }

    setShowLogin(true)
    window.scrollTo(0, 0)
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'

    return () => {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
  }, [])

  const [fetchOrder, {loading}] = useLazyQuery(OrdersByTikTokOrderIdQuery, {
    fetchPolicy: 'network-only',
    onCompleted(data) {
      const [order] = data?.getOrdersByTikTokOrderId || []
      if (!order) {
        setError(
          `No orders were found matching your TikTok Order ID. If you believe this is a mistake, please refresh your browser and try again.`,
        )
        return
      }

      const nextState = {
        ...state,
        authenticated: true,
        orderNumber: order.source_id,
        order: order,
        timestamp: Date.now(),
        email: order.email,
      }
      setState(nextState)
      window.localStorage.setItem(
        lookupStorageKey,
        JSON.stringify(nextState),
      )
      window.location.assign(`/order/${order.source_id}`)
    },
    onError() {
      setError(
        `No orders were found matching your TikTok Order ID. If you believe this is a mistake, please refresh your browser and try again.`,
      )
    },
  })

  function submitHandler(e) {
    e.preventDefault()
    setError('')

    if (!state.tiktokOrderId.trim()) {
      setError('TikTok Order ID is required')
      return
    }

    fetchOrder({
      variables: {tiktokOrderId: state.tiktokOrderId.trim()},
    })
  }

  function handleChange(e) {
    setState({
      ...state,
      [e.currentTarget.name]: e.currentTarget.value,
    })
    setError('')
  }

  if (!showLogin) return null

  return (
    <main tw="fixed inset-0 z-50 grid place-content-center overflow-hidden background[linear-gradient(to bottom, #AEF5EE, #fff)]">
      <ShadowBox
        tw="mt-0 max-w-lg sm:w-screen mx-auto flex-1 place-self-center width[calc(100vw - 2rem)]"
        style={{minWidth: 'clamp(300px, 440px, 40vw)'}}
      >
        <header tw="text-center">
          <Image
            alt="Dangle It Logo"
            width="160"
            height="53"
            tw="h-8 w-auto mx-auto text-center"
            src="/logo.png"
            quality={100}
          />
          <H2>Order Lookup</H2>
          <Subtitle>Let&apos;s find your order</Subtitle>
        </header>
        <form onSubmit={submitHandler} tw="mt-4">
          <Div>
            <Label id="tiktokOrderId" text="TikTok Order Number:" />
            <Text
              type="text"
              name="tiktokOrderId"
              onChange={handleChange}
              value={state.tiktokOrderId}
            />
          </Div>

          <div tw="text-center">
            <Button tw="bg-brand text-white rounded py-2 px-16 mx-auto uppercase flex items-center transition-all">
              Go
              {loading && <Loading tw="ml-2" />}
            </Button>
            <a
              tw="underline mt-4 text-xs inline-block opacity-80"
              href="https://dangleit.com/pages/contact-us"
              target="_blank"
              rel="noreferrer"
            >
              Having trouble logging in?
            </a>
          </div>
          {error && (
            <p tw="mt-2 text-sm text-center text-red-600">
              {error}
            </p>
          )}
        </form>
      </ShadowBox>
    </main>
  )
}

Lookup.displayName = 'OrderLookup'
export default Lookup
